import React, { useState } from "react";
import SlidePanel from "../add_customer/SlidePanel";
import { X } from "lucide-react";

const EditNextOfKin = ({
  id_R,
  editNextOfKinOpen,
  handleCloseEditNextOfKin,
  fullName,
  setFullName,
  relationship,
  setRelationship,
  dateOfBirth,
  setDateOfBirth,
  location,
  setLocation,
  mobileNumber,
  setMobileNumber,
  handleEditNextOfKin,
  loading,
}) => {
  const [phoneError, setPhoneError] = useState("");

  const today = new Date()?.toISOString()?.split("T")[0];

  const validatePhoneNumber = (number) => {
    if (!number) {
      setPhoneError("");
      return false;
    }

    if (number?.startsWith("0") && number?.length !== 10) {
      setPhoneError("Phone number starting with 0 must be 10 digits");
      return false;
    }

    if (number?.startsWith("254") && number?.length !== 12) {
      setPhoneError("Phone number starting with 254 must be 12 digits");
      return false;
    }

    if (number?.startsWith("+254") && number?.length !== 13) {
      setPhoneError("Phone number starting with +254 must be 13 characters");
      return false;
    }

    if (
      !number?.startsWith("0") &&
      !number?.startsWith("254") &&
      !number?.startsWith("+254")
    ) {
      setPhoneError("Phone number must start with 0, 254 or +254");
      return false;
    }

    setPhoneError("");
    return true;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    value = value.replace(/(?!^\+)[^\d]/g, "");

    if (value.startsWith("+254")) {
      value = "+" + value?.slice(1)?.replace(/\D/g, "").slice(0, 12);
    } else if (value.startsWith("254")) {
      value = value.replace(/\D/g, "").slice(0, 12);
    } else if (value.startsWith("0")) {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setMobileNumber(value);

    if (value.length > 0) {
      validatePhoneNumber(value);
    } else {
      setPhoneError("");
    }
  };

  const isPhoneValid =
    !!mobileNumber &&
    !phoneError &&
    ((mobileNumber?.startsWith("0") && mobileNumber?.length === 10) ||
      (mobileNumber?.startsWith("254") && mobileNumber?.length === 12) ||
      (mobileNumber?.startsWith("+254") && mobileNumber?.length === 13));

  const allFieldsFilled =
    fullName && relationship && dateOfBirth && location && isPhoneValid;

  return (
    <SlidePanel isOpen={editNextOfKinOpen} onClose={handleCloseEditNextOfKin}>
      <div className="relative flex flex-col h-full bg-white rounded-[12px]">
        <div
          onClick={handleCloseEditNextOfKin}
          className="absolute top-0 right-0 cursor-pointer h-[40px] w-[40px] bg-[#FF0000] rounded-tr-[10px] rounded-bl-[20px] flex items-center justify-center z-10"
        >
          <X color="#ffffff" />
        </div>

        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="mt-8 px-5">
            <div className="flex items-center gap-[10px] mb-[20px]">
              <p className="text-[20px] font-bold text-[#000]">
                Edit next of kin
              </p>
            </div>

            <p className="text-[#000] text-[15px]">
              Please proceed carefully. Updating next of kin information is a
              sensitive action that requires accuracy and responsibility.
            </p>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 pb-6 mt-8">
            <div className="flex flex-col gap-8">
              {/* Full Name + DOB */}

              <div className="flex w-full flex-col">
                <label className="mb-1 text-md font-normal text-[#000]">
                  Full name
                </label>

                <input
                  type="text"
                  placeholder="Enter the full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="input"
                />
              </div>

              <div className="flex w-full flex-col">
                <label className="mb-1 text-md font-normal text-[#000]">
                  Date of birth
                </label>

                <input
                  type="date"
                  value={dateOfBirth}
                  max={today}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="input"
                />
              </div>

              {/* Relationship + Phone */}

              <div className="flex w-full flex-col">
                <label className="mb-1 text-md font-normal text-[#000]">
                  Relationship
                </label>

                <input
                  type="text"
                  placeholder="Enter the relationship"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  className="input"
                />
              </div>

              <div className="flex w-full flex-col">
                <label className="mb-1 text-md font-normal text-[#000]">
                  Phone number
                </label>

                <input
                  type="text"
                  placeholder="Enter the phone number"
                  value={mobileNumber}
                  onChange={handlePhoneChange}
                  className={`input border ${
                    phoneError ? "border-red-500" : "border-transparent"
                  }`}
                />

                {phoneError && (
                  <p className="text-red-500 text-[13px] mt-2">{phoneError}</p>
                )}
              </div>

              {/* Location */}
              <div className="flex gap-4">
                <div className="flex w-full flex-col">
                  <label className="mb-1 text-md font-normal text-[#000]">
                    Location
                  </label>

                  <input
                    type="text"
                    placeholder="Enter the location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 flex justify-end">
            <button
              onClick={handleEditNextOfKin}
              className={`w-full rounded text-[#FFFFFF] px-10 py-2 flex items-center justify-center 
                ${allFieldsFilled ? "bg-[#074073]" : "bg-[#D9D9D9]"} 
                ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
              disabled={loading || !allFieldsFilled}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>

                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
};

export default EditNextOfKin;
